import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Card, CardContent, Typography, Box, useTheme, Skeleton } from '@mui/material';
import { formatYAxisValue } from '../../Components/DrillDown/formatters';
import GenericModal from '../../Components/GenericModal';
import { EvolucaoComprasDrillDown } from '../../Components/DrillDown';
import { useEvolucaoComprasDrillDown } from '../../Hooks/useTransactionDrillDown';

type ComprasHistoricoData = {
  periodo: string;
  total_compras: number;
};

type Props = {
  data: ComprasHistoricoData[];
  loading?: boolean;
  title?: string;
  establishmentId?: number | string | null;
  onMissingEstablishment?: () => void;
};

const EvolucaoComprasChart: React.FC<Props> = ({
  data,
  loading,
  title = 'Evolução das Compras por Mês (Últimos 12 meses)',
  establishmentId,
  onMissingEstablishment
}) => {
  const theme = useTheme();
  const {
    isModalOpen,
    drillDownData,
    loading: drillDownLoading,
    selectedPeriod,
    openModal,
    closeModal
  } = useEvolucaoComprasDrillDown('compras-periodo-drill-down', establishmentId);

  const handleBarClick = async (clickData: any) => {
    if (establishmentId === undefined || establishmentId === null || String(establishmentId).trim() === '') {
      onMissingEstablishment?.();
      return;
    }

    const periodo = clickData.indexValue as string;
    await openModal(periodo);
  };

  return (
    <>
      <Card sx={{ 
 
        p: 2, 
        overflow: 'visible',
        position: 'relative',
        zIndex: 1
      }}>
        <CardContent sx={{ 
          overflow: 'visible',
          position: 'relative',
          zIndex: 2
        }}>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: '#555555' }}>
            {title}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          ) : (
            <div style={{ height: 400, overflow: 'visible', position: 'relative', zIndex: 10 }}>
              <ResponsiveBar
                valueFormat={(value) =>
                  new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(value as number)
                }
                data={data}
                keys={['total_compras']}
                indexBy="periodo"
                margin={{ top: 20, right: 30, bottom: 60, left: 80 }}
                padding={0.3}
                innerPadding={4}
                layout="vertical"
                colors={() => '#1976d2'} // azul fixo
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Compras R$',
                  legendPosition: 'middle',
                  legendOffset: -70,
                  format: formatYAxisValue
                }}
                labelSkipHeight={12}
                labelTextColor="#fff"
                labelFormat={(value) =>
                  `R$ ${Number(value).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}`
                }
                theme={{
                  labels: {
                    text: {
                      fontSize: 14,
                      fontWeight: 'bold',
                    },
                  },
                }}
                tooltip={({ id, value, indexValue }) => (
                  <Box
                    sx={{
                      background: theme.palette.background.paper,

                      p: 2
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {indexValue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(value as number)}
                    </Typography>       
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                        Clique para ver detalhes do período
                      </Typography>
                  </Box>
                )}
                onClick={handleBarClick}
                animate={true}
                motionConfig="gentle"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <GenericModal
        open={isModalOpen}
        onClose={closeModal}
        title={`Análise Detalhada do Período`}
        loading={drillDownLoading}
        showBreadcrumb={true}
        breadcrumbItems={[{ label: selectedPeriod || 'Período' }]}
        maxWidth="xl"
        height="90vh"
      >
        {drillDownData && <EvolucaoComprasDrillDown data={drillDownData} />}
      </GenericModal>
    </>
  );
};

export default EvolucaoComprasChart;
